import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Ciudad } from './ciudad';

export interface CiudadesState {
  list: PaginatedList<Ciudad>;
  current: Ciudad;
  actives: Ciudad[];

  nuevo: () => void;
  cargarPaginadas: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivas: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarCiudad) => Promise<number | null>;
  actualizar: (id: number, request: ActualizarCiudad) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarCiudad {
  nombre: string;
}

export interface ActualizarCiudad {
  nombre: string;
}

const { showSuccessToast } = useUIStore.getState();

export const useCiudadesStore = create<CiudadesState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Ciudad,
  actives: [],

  nuevo: () =>
    set({
      current: {} as Ciudad
    }),
  cargarPaginadas: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<Ciudad>>(
      `/ciudades/paginadas?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivas: async () => {
    const data = await makeGet<Array<Ciudad>>('/ciudades/activas');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<Ciudad>(`/ciudades/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarCiudad) => {
    const data = await makePost<RegistrarCiudad, number | null>('/ciudades/', request);

    if (data.isFailure) return null;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Ciudad
    });

    return data.result;
  },
  actualizar: async (id: number, request: ActualizarCiudad) => {
    const data = await makePatch<ActualizarCiudad, Ciudad>(`/ciudades/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/ciudades/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activa: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/ciudades/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  }
}));
